import { Loader } from 'components/ai-elements/loader';
import React, { useEffect, useState } from 'react';
import { captureArtifactError } from 'sentry/artifact';

type DashboardPreviewProps = {
  url: string;
};

/**
 * Renders a self-contained HTML dashboard generated by the chatbot.
 *
 * The HTML is fetched from the presigned S3 URL and rendered via srcdoc
 * rather than iframe src: the app CSP's frame-src does not allow the chatbot
 * S3 bucket (only connect-src does), fetching once makes the artifact immune
 * to presigned-URL expiry on remount, and fetch failures are observable
 * (a blocked/failed iframe src fails silently with a blank pane).
 *
 * sandbox="allow-scripts" WITHOUT allow-same-origin gives the document an
 * opaque origin — no parent DOM, cookies, or Auth0 tokens. Never add
 * allow-same-origin: with srcdoc that would make this LLM-generated content
 * same-origin with the app itself.
 *
 * As a srcdoc document it inherits the app CSP, so its inline scripts run
 * only while script-src keeps 'unsafe-inline' — revisit if the CSP is
 * tightened.
 */
export const DashboardPreview = ({ url }: DashboardPreviewProps) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState(400);

  // Fetch the HTML document from S3
  useEffect(() => {
    let cancelled = false;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load dashboard: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setHtml(text);
      })
      .catch((err) => {
        if (cancelled) return;
        captureArtifactError(err, { url, kind: 'html' });
        setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Listen for postMessage from the iframe to auto-size height
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (
        event.data?.type === 'dashboard-resize' &&
        typeof event.data.height === 'number'
      ) {
        setIframeHeight(Math.min(event.data.height, 800));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (html === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-muted overflow-hidden bg-white relative">
      <iframe
        srcDoc={html}
        sandbox="allow-scripts"
        title="Dashboard preview"
        className="w-full border-0"
        style={{ height: iframeHeight }}
      />
    </div>
  );
};
